import { useId } from "react";
import { Link as RouterLink } from "react-router-dom";

import PhoneDisabledIcon from "@mui/icons-material/PhoneDisabled";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import type { SxProps, Theme } from "@mui/material/styles";

import {
  standbyNoticeIconSx,
  standbyNoticeSx,
  standbyPopoverSx
} from "features/calls/dialstack/softphone/dialstack-softphone-widget.styles";

import useVoiceSeatNoticeAudience from "./use-voice-seat-notice-audience.hook";
import {
  EMAIL_SUNO_HREF,
  EMAIL_SUNO_LABEL,
  MESSAGE_SUNO_LABEL,
  MESSAGE_SUNO_TEXT,
  OPEN_VOICE_SETUP_LABEL,
  VOICE_SEAT_NOTICE_COPY,
  VOICE_SETUP_USERS_PATH
} from "./voice-seat-notice.copy";

type IntercomShowNewMessage = (command: "showNewMessage", text: string) => void;

// The Intercom messenger is booted for most users but not guaranteed (it loads
// on its own schedule and can be blocked); without it an admin is offered an
// email to Suno instead.
function getIntercom(): IntercomShowNewMessage | null {
  const intercom = (window as unknown as { Intercom?: unknown }).Intercom;
  return typeof intercom === "function"
    ? (intercom as IntercomShowNewMessage)
    : null;
}

type VoiceSeatNoticeProps = {
  // "popover" sits where the phone would open (the corner FAB); "page" stands
  // in for a Voice screen reached without access.
  variant?: "popover" | "page";
  // The title's id, so a host (the popover) can name itself after it.
  titleId?: string;
  // Called after the notice's own action runs, so a host popover can close.
  onAction?: () => void;
};

// What a staff member without Voice sees instead of the phone or a Voice
// screen (ENG-2315): why, and the one thing they can do about it.
export default function VoiceSeatNotice({
  variant = "popover",
  titleId,
  onAction
}: VoiceSeatNoticeProps) {
  const audience = useVoiceSeatNoticeAudience();
  const generatedTitleId = useId();
  const headingId = titleId ?? generatedTitleId;
  const { title, body } = VOICE_SEAT_NOTICE_COPY[audience];
  const isPage = variant === "page";
  const buttonSize = isPage ? "medium" : "small";
  const intercom = audience === "admin" ? getIntercom() : null;

  let action = null;
  if (audience === "admin") {
    action = intercom ? (
      <Button
        size={buttonSize}
        variant="contained"
        onClick={() => {
          intercom("showNewMessage", MESSAGE_SUNO_TEXT);
          onAction?.();
        }}
      >
        {MESSAGE_SUNO_LABEL}
      </Button>
    ) : (
      <Button
        href={EMAIL_SUNO_HREF}
        size={buttonSize}
        variant="contained"
        onClick={() => onAction?.()}
      >
        {EMAIL_SUNO_LABEL}
      </Button>
    );
  } else if (audience === "suno") {
    action = (
      <Button
        component={RouterLink}
        size={buttonSize}
        to={VOICE_SETUP_USERS_PATH}
        variant="contained"
        onClick={() => onAction?.()}
      >
        {OPEN_VOICE_SETUP_LABEL}
      </Button>
    );
  }

  if (isPage) {
    // A section under the shell's own page title ("Calls"), not a second h1.
    return (
      <Box aria-labelledby={headingId} component="section" sx={pageSx}>
        <Box aria-hidden sx={[standbyNoticeIconSx, pageIconSx]}>
          <PhoneDisabledIcon sx={{ fontSize: 32 }} />
        </Box>
        <Typography component="h2" id={headingId} variant="h5">
          {title}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {body}
        </Typography>
        {action}
      </Box>
    );
  }

  return (
    <Box sx={standbyPopoverSx}>
      <Box sx={standbyNoticeSx}>
        <Box aria-hidden sx={standbyNoticeIconSx}>
          <PhoneDisabledIcon fontSize="small" />
        </Box>
        <Box>
          <Typography
            fontWeight="fontWeightBold"
            id={headingId}
            variant="body2"
          >
            {title}
          </Typography>
          <Typography color="text.secondary" variant="body1">
            {body}
          </Typography>
        </Box>
      </Box>
      {action}
    </Box>
  );
}

const pageSx: SxProps<Theme> = {
  flex: 1,
  minHeight: 360,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 1.5,
  p: 3,
  textAlign: "center"
};

const pageIconSx: SxProps<Theme> = { width: 56, height: 56, mb: 0.5 };
